import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import ScrollReveal from "../components/ScrollReveal";
import CTASection from "../components/sections/CTASection";
import { BLOG_POSTS } from "../data/content";

export default function BlogDetailPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div data-testid="blog-detail-page">
      <article className="pt-32 md:pt-40 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/50 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={14} /> Back to Blog
          </Link>

          <ScrollReveal>
            <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
              {post.date} · {post.readTime} · {post.author}
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
              {post.title}
            </h1>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={0.1} className="max-w-5xl mx-auto px-6 md:px-10 mt-12 mb-16">
          <div className="relative aspect-[16/9] overflow-hidden border border-white/10">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </ScrollReveal>

        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-6">
          {post.body.map((para, idx) => (
            <ScrollReveal key={idx} delay={idx * 0.05}>
              <p className="text-white/75 text-lg leading-relaxed font-light">
                {para}
              </p>
            </ScrollReveal>
          ))}
        </div>
      </article>

      {/* Related */}
      <section className="py-20 md:py-28 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-10 text-xs uppercase tracking-[0.4em] text-white/40">
            / Keep Reading
          </div>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {others.map((p, i) => (
              <ScrollReveal key={p.slug} delay={i * 0.05}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-related-${p.slug}`}
                  className="group block border border-white/10 bg-[#080808] hover:border-white/30 transition-colors"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-2">
                      {p.date}
                    </div>
                    <h3 className="font-display text-lg md:text-xl font-extrabold uppercase tracking-tight leading-[1.15] group-hover:text-white/70 transition-colors">
                      {p.title}
                    </h3>
                    <div className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/60">
                      Read <ArrowUpRight size={12} />
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
